import Request from '../../utilities/Request';
import { retrieveAccounts } from './accounts.js';

export const IMPORT_UPLOAD_BEGIN = 'IMPORT_UPLOAD_BEGIN';
export const IMPORT_UPLOAD_SUCCESS = 'IMPORT_UPLOAD_SUCCESS';
export const IMPORT_UPLOAD_FAIL = 'IMPORT_UPLOAD_FAIL';

export const IMPORT_MONARCH_FETCH_BEGIN = 'IMPORT_MONARCH_FETCH_BEGIN';
export const IMPORT_MONARCH_BALANCES_RECORDED = 'IMPORT_MONARCH_BALANCES_RECORDED';
export const IMPORT_MONARCH_CONNECT_BEGIN = 'IMPORT_MONARCH_CONNECT_BEGIN';
export const IMPORT_MONARCH_CONNECTION_CHANGED = 'IMPORT_MONARCH_CONNECTION_CHANGED';
export const IMPORT_MONARCH_REFRESH_BEGIN = 'IMPORT_MONARCH_REFRESH_BEGIN';
export const IMPORT_MONARCH_REFRESH_SUCCESS = 'IMPORT_MONARCH_REFRESH_SUCCESS';
export const IMPORT_MONARCH_REFRESH_FAIL = 'IMPORT_MONARCH_REFRESH_FAIL';
export const IMPORT_MONARCH_RULES_LOADED = 'IMPORT_MONARCH_RULES_LOADED';

export const IMPORT_BATCHES_LOAD_BEGIN = 'IMPORT_BATCHES_LOAD_BEGIN';
export const IMPORT_BATCHES_LOAD_SUCCESS = 'IMPORT_BATCHES_LOAD_SUCCESS';

export const IMPORT_ACCOUNTS_LOAD_BEGIN = 'IMPORT_ACCOUNTS_LOAD_BEGIN';
export const IMPORT_ACCOUNTS_LOAD_SUCCESS = 'IMPORT_ACCOUNTS_LOAD_SUCCESS';

export const IMPORT_PREVIEW_LOAD_BEGIN = 'IMPORT_PREVIEW_LOAD_BEGIN';
export const IMPORT_PREVIEW_LOAD_SUCCESS = 'IMPORT_PREVIEW_LOAD_SUCCESS';

export const IMPORT_CATEGORIES_LOAD_BEGIN = 'IMPORT_CATEGORIES_LOAD_BEGIN';
export const IMPORT_CATEGORIES_LOAD_SUCCESS = 'IMPORT_CATEGORIES_LOAD_SUCCESS';

export const IMPORT_CATEGORY_ROWS_OPENED = 'IMPORT_CATEGORY_ROWS_OPENED';
export const IMPORT_CATEGORY_ROWS_CLOSED = 'IMPORT_CATEGORY_ROWS_CLOSED';
export const IMPORT_CATEGORY_ROWS_LOAD_BEGIN = 'IMPORT_CATEGORY_ROWS_LOAD_BEGIN';
export const IMPORT_CATEGORY_ROWS_LOAD_SUCCESS = 'IMPORT_CATEGORY_ROWS_LOAD_SUCCESS';

export const IMPORT_BALANCES_LOAD_BEGIN = 'IMPORT_BALANCES_LOAD_BEGIN';
export const IMPORT_BALANCES_LOAD_SUCCESS = 'IMPORT_BALANCES_LOAD_SUCCESS';
export const IMPORT_BACKFILL_BEGIN = 'IMPORT_BACKFILL_BEGIN';

export const IMPORT_COMMIT_BEGIN = 'IMPORT_COMMIT_BEGIN';
export const IMPORT_COMMIT_SUCCESS = 'IMPORT_COMMIT_SUCCESS';

export const IMPORT_BATCH_SELECTED = 'IMPORT_BATCH_SELECTED';
export const IMPORT_BATCH_DELETED = 'IMPORT_BATCH_DELETED';
export const IMPORT_ERROR_DISMISSED = 'IMPORT_ERROR_DISMISSED';

/**
 * The API layer only reads $_POST/$_GET, so the browser reads the file and posts its text rather
 * than sending a multipart upload the domain would never see.
 */
export const uploadImportFile = (file) => (dispatch) => {
	dispatch({ type: IMPORT_UPLOAD_BEGIN, payload: file.name });

	return readFileAsText(file)
		.then((contents) => Request.apiv1Post('import/upload', {
			filename: file.name,
			contents,
		}))
		.then((response) => {
			dispatch({ type: IMPORT_UPLOAD_SUCCESS, payload: response });
			return dispatch(loadBatchDetail(response.batch));
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/**
 * Fetches from Monarch's API and stages it as a batch to review, as an upload is -- everything, unless
 * a first day is given. It lands the same way an upload does, so the screen after it is the same one.
 *
 * The server records each mapped account's balance on the way, and answers what it did as `balances`
 * (with `error` when that part failed, which leaves the batch staged), and the connection as
 * `monarch_connection`, since either fetch can find the token refused. A refusal carries both too
 * when it got that far: one for there being nothing to stage comes after the balances were written.
 */
export const fetchFromMonarch = (start = 'all') => (dispatch) => {
	dispatch({ type: IMPORT_MONARCH_FETCH_BEGIN });

	return Request.apiv1Post('import/fetch_monarch', { start })
		.then((response) => {
			dispatch({ type: IMPORT_UPLOAD_SUCCESS, payload: response });
			recordMonarchFetch(dispatch, response, false);
			return dispatch(loadBatchDetail(response.batch));
		})
		.catch((error) => {
			recordMonarchFetch(dispatch, (error && error.responseJSON) || {}, true);
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/**
 * The budget selected now, by id, or null when the store does not say. A Monarch answer belongs to
 * the budget its request was made under: one that lands after a switch is another budget's, and is
 * dropped rather than shown on this one.
 */
export function activeBudgetId(getState) {
	const state = typeof getState === 'function' ? getState() : null;
	const budget = state && state.dashboard && state.dashboard.player && state.dashboard.player.active_budget;
	return budget ? budget.id : null;
}

/** Calls `use` with the answer only if the budget is still the one the request was made under. */
function whileBudget(getState, budgetId, use) {
	return (answer) => (activeBudgetId(getState) === budgetId ? use(answer) : undefined);
}

/**
 * The Accounts page's "Refresh balances": one request to Monarch for its accounts, recording each
 * mapped one's balance (Import_Domain::refresh_monarch_balances). It answers `balances` and
 * `monarch_connection` as a fetch does, and is kept the same way. Its failures are the Accounts
 * page's own, kept apart from the import page's error, and a refusal still carries the connection
 * when the token was refused. A 429 carries `retry_after`, which holds the button off until then.
 * Resolves true when the balances were recorded.
 */
export const refreshMonarchBalances = () => (dispatch, getState) => {
	const budgetId = activeBudgetId(getState);
	dispatch({ type: IMPORT_MONARCH_REFRESH_BEGIN });

	return Request.apiv1Post('import/refresh_monarch_balances', {})
		.then(whileBudget(getState, budgetId, (response) => {
			dispatch({ type: IMPORT_MONARCH_REFRESH_SUCCESS, payload: response });
			recordMonarchFetch(dispatch, response, false);
			return true;
		}))
		.catch(whileBudget(getState, budgetId, (error) => {
			const body = (error && error.responseJSON) || {};
			recordMonarchFetch(dispatch, body, true);
			const retryAfter = Number(body.retry_after) || 0;
			dispatch({
				type: IMPORT_MONARCH_REFRESH_FAIL,
				payload: errorMessage(error),
				blockedUntil: retryAfter > 0 ? Date.now() + retryAfter * 1000 : null,
			});
			return false;
		}))
		.then((refreshed) => refreshed === true);
};

/**
 * Whether this budget is connected to Monarch, for the Accounts page: the batch list's
 * `monarch_connection`, as the import page reads it. A failure says nothing, so the import page's
 * error is never set from the Accounts page; the button simply stays away.
 */
export const loadMonarchConnection = () => (dispatch, getState) => {
	const budgetId = activeBudgetId(getState);
	return Request.apiv1Get('import/batches')
		.then(whileBudget(getState, budgetId, (response) => {
			dispatch({ type: IMPORT_MONARCH_CONNECTION_CHANGED, payload: response.monarch_connection });
		}))
		.catch(() => {});
};

/**
 * Maps one of Monarch's accounts, as a refresh listed it, without a batch: Import_Domain's
 * save_account_rule, which is the same rule a batch's mapper saves, keyed on the account's name. It
 * answers with every rule, and the listed accounts take their maps from that, so nothing asks Monarch
 * again. When the refresh was recent the server also writes the account's balance from what that
 * refresh read, and answers the new summary as `balances`. `action` is 'assign' or 'skip'.
 *
 * Resolves `{ saved, error }`: a refusal is the row's to show, not the page's.
 */
export const saveMonarchAccountRule = (externalKey, plaidAccountId, action = 'assign') => (dispatch, getState) => {
	return monarchRuleRequest(dispatch, getState, 'import/save_account_rule', {
		external_key: externalKey,
		plaid_account_id: action === 'assign' ? plaidAccountId : null,
		action,
	});
};

export const clearMonarchAccountRule = (externalKey) => (dispatch, getState) => {
	return monarchRuleRequest(dispatch, getState, 'import/clear_account_rule', { external_key: externalKey });
};

/**
 * Creates an account for one of Monarch's and maps it there (create_account_for_rule), then re-reads
 * the Accounts page, which now has one more account.
 */
export const createMonarchAccount = (externalKey, account) => (dispatch, getState) => {
	return monarchRuleRequest(dispatch, getState, 'import/create_account_for_rule', {
		external_key: externalKey,
		name: account.name,
		type: account.type,
		mask: account.mask,
	}, true);
};

function monarchRuleRequest(dispatch, getState, action, data, addsAccount = false) {
	const budgetId = activeBudgetId(getState);
	const dropped = { saved: false, error: null };
	return Request.apiv1Post(action, data)
		.then(whileBudget(getState, budgetId, (response) => {
			dispatch({ type: IMPORT_MONARCH_RULES_LOADED, payload: response });
			const reread = recordMonarchFetch(dispatch, response, false);
			if(addsAccount && !reread) {
				dispatch(retrieveAccounts());
			}
			return { saved: true, error: null };
		}))
		.catch(whileBudget(getState, budgetId, (error) => ({ saved: false, error: errorMessage(error) })))
		.then((result) => result || dropped);
}

/**
 * Keeps what a fetch's answer says of the connection and the balances, and re-reads the accounts list
 * when any balance was written, so the Accounts page shows them without a reload. On a refusal the
 * server's message already says why the balances failed, so that is not repeated as a line of its own.
 */
function recordMonarchFetch(dispatch, body, refused) {
	if(body.monarch_connection !== undefined) {
		dispatch({ type: IMPORT_MONARCH_CONNECTION_CHANGED, payload: body.monarch_connection });
	}
	if(body.balances === undefined) {
		return false;
	}
	const balances = refused && body.balances && body.balances.error ? null : body.balances;
	dispatch({ type: IMPORT_MONARCH_BALANCES_RECORDED, payload: balances || null });
	if(balances && balances.written > 0) {
		dispatch(retrieveAccounts());
		return true;
	}
	return false;
}

/**
 * Signs this budget in to Monarch (Import_Domain::connect_monarch). The server keeps only the token
 * Monarch answers with, so the password and code go in this one request and nowhere else.
 */
export const connectMonarch = ({ email, password, code }) => (dispatch, getState) => {
	const budgetId = activeBudgetId(getState);
	dispatch({ type: IMPORT_MONARCH_CONNECT_BEGIN });

	return Request.apiv1Post('import/connect_monarch', { email, password, code: code || '' })
		.then(whileBudget(getState, budgetId, (response) => {
			dispatch({ type: IMPORT_MONARCH_CONNECTION_CHANGED, payload: response.monarch_connection });
			return true;
		}))
		.catch(whileBudget(getState, budgetId, (error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
			return false;
		}))
		.then((connected) => connected === true);
};

export const disconnectMonarch = () => (dispatch, getState) => {
	const budgetId = activeBudgetId(getState);
	return Request.apiv1Post('import/disconnect_monarch', {})
		.then(whileBudget(getState, budgetId, () => {
			dispatch({ type: IMPORT_MONARCH_CONNECTION_CHANGED, payload: null });
		}))
		.catch(whileBudget(getState, budgetId, (error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		}));
};

export const loadImportBatches = () => (dispatch, getState) => {
	const budgetId = activeBudgetId(getState);
	dispatch({ type: IMPORT_BATCHES_LOAD_BEGIN });
	return Request.apiv1Get('import/batches')
		.then(whileBudget(getState, budgetId, (response) => {
			dispatch({
				type: IMPORT_BATCHES_LOAD_SUCCESS,
				payload: response.batches,
				monarchConnection: response.monarch_connection,
			});
		}))
		.catch(whileBudget(getState, budgetId, (error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		}));
};

export const selectImportBatch = (batchId) => (dispatch, getState) => {
	dispatch({ type: IMPORT_BATCH_SELECTED, payload: batchId });
	if(!batchId) {
		return Promise.resolve();
	}
	return dispatch(loadImportAccounts(batchId))
		.then(() => dispatch(loadBatchDetail(getState().importing.selectedBatch)));
};

/**
 * The two kinds of upload have nothing to show each other: a transaction file has a row-by-row
 * preview and a balance file has a reconciliation. Every path that lands on a batch comes through
 * here so neither one has to remember which it is looking at.
 */
export const loadBatchDetail = (batch) => (dispatch) => {
	if(!batch) {
		return Promise.resolve();
	}
	if(batch.kind === 'balances') {
		return dispatch(loadImportBalances(batch.id));
	}
	/**
	 * The preview and the category list are two readings of the same decisions, so they are fetched
	 * together rather than left to drift: a mapping saved here changes both, and a preview showing
	 * rows as blocked next to a mapper that says they are mapped is worse than either alone.
	 */
	return Promise.all([
		/** A filter belongs to the file it was chosen on, so landing on a batch starts unfiltered. */
		dispatch(loadImportPreview(batch.id, 0, null)),
		dispatch(loadImportCategories(batch.id)),
	]);
};

export const loadImportAccounts = (batchId) => (dispatch) => {
	dispatch({ type: IMPORT_ACCOUNTS_LOAD_BEGIN });
	return Request.apiv1Get(`import/accounts/${batchId}`)
		.then((response) => {
			dispatch({ type: IMPORT_ACCOUNTS_LOAD_SUCCESS, payload: response });
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/**
 * A page of the preview, optionally narrowed to one status -- the summary tiles ask for that.
 *
 * Leaving `status` out keeps whatever filter is on screen, so a mapping saved while looking at the
 * blocked rows re-reads the blocked rows and the list visibly shrinks. Pass null to clear it.
 */
export const loadImportPreview = (batchId, offset = 0, status) => (dispatch, getState) => {
	if(status === undefined) {
		status = getState ? getState().importing.previewStatus : null;
	}
	const parameters = { batch_id: batchId, offset };
	/** Left out rather than sent as null, which a GET would carry as the word "null". */
	if(status) {
		parameters.status = status;
	}
	dispatch({ type: IMPORT_PREVIEW_LOAD_BEGIN });
	return Request.apiv1Get('import/preview', parameters)
		.then((response) => {
			dispatch({ type: IMPORT_PREVIEW_LOAD_SUCCESS, payload: response });
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

export const loadImportCategories = (batchId) => (dispatch) => {
	dispatch({ type: IMPORT_CATEGORIES_LOAD_BEGIN });
	return Request.apiv1Get(`import/categories/${batchId}`)
		.then((response) => {
			dispatch({ type: IMPORT_CATEGORIES_LOAD_SUCCESS, payload: response });
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/** How many rows the popup over the category mapper shows at a time. */
export const CATEGORY_ROWS_PAGE_SIZE = 25;

/**
 * Opens the popup on one line of the category mapper -- a whole export category, or a single
 * merchant inside it -- and fetches its first page.
 *
 * `externalMerchant` is null for the whole category and a name, possibly '', for one merchant. The
 * empty string is not "no merchant was asked for": it is the rows the file gave no merchant at all,
 * which are a group of their own that the mapper lists but cannot give a rule to.
 */
export const openImportCategoryRows = (batchId, externalCategory, externalMerchant = null) => (dispatch) => {
	dispatch({ type: IMPORT_CATEGORY_ROWS_OPENED, payload: { externalCategory, externalMerchant } });
	return dispatch(loadImportCategoryRows(batchId, externalCategory, externalMerchant, 0));
};

export const closeImportCategoryRows = () => ({ type: IMPORT_CATEGORY_ROWS_CLOSED });

export const loadImportCategoryRows = (batchId, externalCategory, externalMerchant, offset = 0) => (dispatch) => {
	dispatch({ type: IMPORT_CATEGORY_ROWS_LOAD_BEGIN });
	return Request.apiv1Get('import/category_rows', {
			batch_id: batchId,
			external_category: externalCategory,
			/**
			 * Left out rather than sent as null, which is the whole difference between asking for the
			 * category and asking for one merchant in it: a GET has no nulls, and null would arrive as
			 * the string 'null' -- a merchant name matching nothing, and an empty popup.
			 */
			external_merchant: externalMerchant === null ? undefined : externalMerchant,
			limit: CATEGORY_ROWS_PAGE_SIZE,
			offset,
		})
		.then((response) => {
			dispatch({ type: IMPORT_CATEGORY_ROWS_LOAD_SUCCESS, payload: response });
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/**
 * Saving a mapping re-reads the preview, because one account decision can move hundreds of rows
 * between blocked and ready and the counts would otherwise go stale.
 */
export const saveImportAccountMap = (batchId, externalKey, plaidAccountId, action = 'assign') => (dispatch) => {
	return Request.apiv1Post('import/save_account_map', {
			batch_id: batchId,
			external_key: externalKey,
			plaid_account_id: plaidAccountId,
			action,
		})
		.then((response) => {
			dispatch({ type: IMPORT_ACCOUNTS_LOAD_SUCCESS, payload: response });
			return dispatch(loadBatchDetail(response.batch));
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/**
 * Creates a budget account for a label that matches nothing here, and maps the label onto it in the
 * same request -- creating one and then leaving it unmapped would be a step nobody wants.
 */
export const createImportAccount = (batchId, externalKey, account) => (dispatch) => {
	return Request.apiv1Post('import/create_account', {
			batch_id: batchId,
			external_key: externalKey,
			name: account.name,
			type: account.type,
			mask: account.mask,
		})
		.then((response) => {
			dispatch({ type: IMPORT_ACCOUNTS_LOAD_SUCCESS, payload: response });
			return dispatch(loadBatchDetail(response.batch));
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

export const clearImportAccountMap = (batchId, externalKey) => (dispatch) => {
	return Request.apiv1Post('import/clear_account_map', {
			batch_id: batchId,
			external_key: externalKey,
		})
		.then((response) => {
			dispatch({ type: IMPORT_ACCOUNTS_LOAD_SUCCESS, payload: response });
			return dispatch(loadBatchDetail(response.batch));
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/**
 * A category rule: either one covering a whole export category, or a narrower one naming a single
 * merchant within it. `externalMerchant` is '' for the first, which is what the endpoint defaults to
 * and what the unique key is built on.
 *
 * The three actions are the ones Import_Category_Rule defines -- assign to a category, take the bank
 * record without a ledger transaction, or leave the rows out entirely. Only 'assign' carries a
 * category id; the other two are decisions about rows that should never reach a budget at all.
 *
 * The endpoint answers with the category list, and the preview is re-read separately: a single rule
 * can move hundreds of rows out of "needs a category", and the counts on screen would otherwise
 * still be describing the file as it was before the click.
 */
export const saveImportCategoryMap = (batchId, externalCategory, externalMerchant, categoryId, action = 'assign') => (dispatch) => {
	return Request.apiv1Post('import/save_category_map', {
			batch_id: batchId,
			external_category: externalCategory,
			external_merchant: externalMerchant,
			category_id: action === 'assign' ? categoryId : null,
			action,
		})
		.then((response) => {
			dispatch({ type: IMPORT_CATEGORIES_LOAD_SUCCESS, payload: response });
			return dispatch(loadImportPreview(batchId));
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/**
 * Removes one rule. Clearing a merchant's rule leaves it following the category's again; clearing
 * the category's puts every row it covered back to needing a decision.
 */
export const clearImportCategoryMap = (batchId, externalCategory, externalMerchant) => (dispatch) => {
	return Request.apiv1Post('import/clear_category_map', {
			batch_id: batchId,
			external_category: externalCategory,
			external_merchant: externalMerchant,
		})
		.then((response) => {
			dispatch({ type: IMPORT_CATEGORIES_LOAD_SUCCESS, payload: response });
			return dispatch(loadImportPreview(batchId));
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/**
 * Decides what one transaction becomes, ahead of the rule for its (category, merchant) pair. Keyed
 * on the export's id for it rather than on the staged row, so the decision holds when the same
 * transaction is fetched again.
 *
 * One row changing can unblock a category line and change the popup it was chosen in, so all three
 * readings of the batch are re-read rather than just the one on screen.
 */
export const saveImportRowOverride = (batchId, externalId, categoryId, action = 'assign') => (dispatch, getState) => {
	return Request.apiv1Post('import/save_row_override', {
			external_id: externalId,
			category_id: action === 'assign' ? categoryId : null,
			action,
		})
		.then(() => dispatch(reloadAfterRowOverride(batchId, getState)))
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/** Hands one transaction back to whatever rule covers its pair. */
export const clearImportRowOverride = (batchId, externalId) => (dispatch, getState) => {
	return Request.apiv1Post('import/clear_row_override', { external_id: externalId })
		.then(() => dispatch(reloadAfterRowOverride(batchId, getState)))
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

const reloadAfterRowOverride = (batchId, getState) => (dispatch) => {
	const open = getState ? getState().importing.categoryRows : null;
	return Promise.all([
		dispatch(loadImportPreview(batchId)),
		dispatch(loadImportCategories(batchId)),
		open ? dispatch(loadImportCategoryRows(batchId, open.externalCategory, open.externalMerchant, open.offset)) : null,
	]);
};

/**
 * The balance file's own screen: what it would backfill, and how it squares with a transaction
 * export. `againstBatchId` picks which transaction upload to check against; the server falls back to
 * the most recent one when it is left out.
 */
export const loadImportBalances = (batchId, againstBatchId = null) => (dispatch) => {
	dispatch({ type: IMPORT_BALANCES_LOAD_BEGIN });
	const parameters = { batch_id: batchId };
	if(againstBatchId) {
		parameters.against_batch_id = againstBatchId;
	}
	return Request.apiv1Get('import/balances', parameters)
		.then((response) => {
			dispatch({ type: IMPORT_BALANCES_LOAD_SUCCESS, payload: response });
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/**
 * Writes the balance history. Unlike everything else on this screen this one does write -- but into
 * the record of what the accounts were worth, never into the ledger -- and it can be run again
 * safely, which is why nothing here guards against a second click.
 */
export const backfillImportBalances = (batchId) => (dispatch) => {
	dispatch({ type: IMPORT_BACKFILL_BEGIN });
	return Request.apiv1Post('import/backfill_balances', { batch_id: batchId })
		.then((response) => {
			dispatch({ type: IMPORT_BALANCES_LOAD_SUCCESS, payload: response });
			return dispatch(loadImportBatches());
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

/**
 * Deleting a file takes its staged rows with it but not the account and category mappings it
 * produced -- those are keyed on the budget and are worth keeping for the next export.
 */
/**
 * Writes the batch into the budget. This is the one step of an import that deleting the upload will
 * not undo, so the screen asks before dispatching it.
 *
 * The endpoint answers with a full preview payload as well as the result, because committing
 * changes what the preview says -- every row it wrote comes back as already imported. That is also
 * why running this twice is safe, and why it is the way to finish a file that was committed while
 * some of its categories were still unmapped: map what was blocking it and commit again.
 *
 * The batch list is re-read because a fully committed batch changes status, and the list is what
 * shows it.
 */
export const commitImportBatch = (batchId) => (dispatch) => {
	dispatch({ type: IMPORT_COMMIT_BEGIN });
	return Request.apiv1Post('import/commit', { batch_id: batchId })
		.then((response) => {
			dispatch({ type: IMPORT_COMMIT_SUCCESS, payload: response });
			return dispatch(loadImportBatches());
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

export const deleteImportBatch = (batchId) => (dispatch) => {
	return Request.apiv1Post('import/delete_batch', { batch_id: batchId })
		.then((response) => {
			dispatch({ type: IMPORT_BATCH_DELETED, payload: { batchId, batches: response.batches } });
		})
		.catch((error) => {
			dispatch({ type: IMPORT_UPLOAD_FAIL, payload: errorMessage(error) });
		});
};

export const dismissImportError = () => ({ type: IMPORT_ERROR_DISMISSED });

function readFileAsText(file) {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result);
		reader.onerror = () => reject(new Error('That file could not be read.'));
		reader.readAsText(file);
	});
}

function errorMessage(error) {
	if(error && error.responseJSON && error.responseJSON.error) {
		return error.responseJSON.error;
	}
	if(error && error.error) {
		return error.error;
	}
	if(error && error.message) {
		return error.message;
	}
	return 'Something went wrong with the import.';
}
